import React, { PropsWithChildren, useState } from 'react';
import { Action, ThunkDispatch } from '@reduxjs/toolkit';
import { useDispatch, useSelector } from 'react-redux';

import { logOut } from '../../actions/authenticationActions';
import { setLogOutReason } from '../../logOutReason';
import { getLoggedInUser } from '../../selectors/users';
import { LoginDataSliceState } from '../../store/logInDataSlice';
import { LoginScreen, LoginScreenProps } from './LoginScreen';
import StandardLoginScreenContents, { LogInPanel, LoginScreenContentsProps } from './StandardLoginScreenContents';

interface Props extends PropsWithChildren<LoginScreenProps & LoginScreenContentsProps> {
  readonly requirePermission?: string;

  /**
   * Some apps may not require logging in to see certain resources if certain conditions are met, but once those
   * conditions fail to be met, the login container should kick in.
   */
  readonly allowAnonymous?: boolean;

  readonly additionalLoginScreenContent?: (panel: LogInPanel) => React.ReactNode;
}

export function LoginContainer({
  children,
  requirePermission,
  allowAnonymous,
  additionalLoginScreenContent,
  ...loginProps
}: Props) {
  const dispatch = useDispatch<ThunkDispatch<LoginDataSliceState, void, Action>>();

  const automaticLoginOngoing = useSelector((state: LoginDataSliceState) => state.logInData.isAutomaticLogInOngoing);

  const currentUser = useSelector(getLoggedInUser);
  const needsReset = useSelector((state: LoginDataSliceState) => state.logInData.needsPasswordChange);

  const [accountCreatedInCurrentFlow, setAccountCreatedInCurrentFlow] = useState(false);

  if (automaticLoginOngoing) return null;

  if ((currentUser == null && !allowAnonymous) || needsReset || accountCreatedInCurrentFlow)
    return (
      <LoginScreen {...loginProps}>
        <StandardLoginScreenContents
          {...loginProps}
          additionalContent={additionalLoginScreenContent}
          onAccountCreated={() => {
            setAccountCreatedInCurrentFlow(true);
            loginProps.onAccountCreated?.();
          }}
        />
      </LoginScreen>
    );

  if (requirePermission && currentUser ? !currentUser.permissions.includes(requirePermission) : !allowAnonymous) {
    setLogOutReason('That account cannot access this application.', 'warning');
    dispatch(logOut());

    return null;
  }

  return <>{children}</>;
}
